import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import Fuse from "fuse.js";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { DashboardLayout } from "component/layout/dashboard";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { Agent } from "model/agent";
import { getAgents } from "redux/selector";
import { RootState } from "redux/store";
import * as env from "shared/env";
import { PageProps } from "shared/page-props";
import { AgentTable } from "./agent-table";
import { AgentTable as AgentTableRefresh } from "./agent-table-refresh";
import { styles } from "./style";
import MuiAlert from "@mui/material/Alert";
import queryString from "query-string";

interface Props
	extends ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles> {}

interface State {
	term: string;
	showSuccessBanner: boolean;
}

const mapStateToProps = (state: RootState) => {
	const agents = getAgents(state);
	const options: Fuse.IFuseOptions<Agent> = {
		shouldSort: true,
		threshold: 0.5,
		location: 0,
		distance: 100,
		minMatchCharLength: 1,
		keys: ["id", "firstName", "lastName", "email"],
	};
	return {
		agents,
		fuse: new Fuse(agents, options),
		loading: state.agents.loading,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({}, dispatch);

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			term: "",
			showSuccessBanner: false,
		};
	}

	public componentDidMount() {
		this.checkForSuccessBanner();
	}

	public componentDidUpdate(prevProps: Props) {
		if (prevProps.location.search !== this.props.location.search) {
			this.checkForSuccessBanner();
		}
	}

	private checkForSuccessBanner = () => {
		const { location, history } = this.props;
		const params = queryString.parse(location.search);
		if (params.success === "true") {
			this.setState({ showSuccessBanner: true });
			// Remove the success param from URL
			history.replace(location.pathname);
		}
	}

	private renderRefresh() {
		const { loading, user, agents, classes = {} } = this.props;
		const { showSuccessBanner } = this.state;
		const maxForTeams = user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures;
		const title = maxForTeams ? "Team" : "Agents";
		return (
			<DashboardLayout
				permitted={user && user.permissions.broker}
				title={title}
				header={
					<Mui.Grid container justifyContent="space-between" alignItems="center">
						<Mui.Grid item>
							<Mui.Typography variant="h1">{title}</Mui.Typography>
						</Mui.Grid>
					</Mui.Grid>
				}
			>
				<div className={classes.refreshSurface}>
					<Mui.Box mb={3}>
						<Mui.Typography className={classes.pageDescription}>
							Manage members on your team, including their roles, profile information, and account settings.
						</Mui.Typography>
					</Mui.Box>
					{loading ? (
						<LoadingIndicator />
					) : (
						<>
							{showSuccessBanner && (
								<Mui.Box mb={2}>
									<MuiAlert severity="success" onClose={() => this.setState({ showSuccessBanner: false })}>
										Team member added successfully!
									</MuiAlert>
								</Mui.Box>
							)}
							<AgentTableRefresh agents={agents} maxForTeams={maxForTeams} />
						</>
					)}
				</div>
			</DashboardLayout>
		);
	}

	private renderLegacy() {
		const { classes = {}, fuse, loading, user } = this.props;
		let { agents } = this.props;
		const { term, showSuccessBanner } = this.state;
		const maxForTeams = user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures;
		const title = maxForTeams ? "Manage Team" : "Manage Agents";
		if (term) {
			agents = fuse.search(term).map((result) => result.item);
		}
		const placeholder = maxForTeams ? "Search for team member" : "Search for agent"
		return (
			<DashboardLayout
				permitted={user && user.permissions.broker}
				title={title}
				header={
					<Mui.Grid container justifyContent="space-between" alignItems="center">
						<Mui.Grid item xs={3}>
							<Mui.Typography variant="h1">
								<FeatherIcon>
									<Icons.Settings />
								</FeatherIcon>
								{title}
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Hidden xsDown>
							<Mui.Grid item xs={6}>
								<Mui.InputBase
									placeholder={placeholder}
									fullWidth
									onChange={(event) => {
										this.setState({
											term: event.target.value,
										});
									}}
									value={term}
									className={classes.searchBar}
								/>
							</Mui.Grid>
						</Mui.Hidden>
					</Mui.Grid>
				}
			>
				<FullPageCard>
					<Mui.Grid container direction="column" spacing={2}>
						{loading ? (
							<Mui.Grid item>
								<LoadingIndicator />
							</Mui.Grid>
						) : (
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Hidden smUp>
									<Mui.Grid item container justifyContent="space-between" alignItems="center">
										<Mui.Grid item xs={5}>
											<Mui.InputBase
												placeholder="Search for lead"
												fullWidth
												onChange={(event) => {
													this.setState({
														term: event.target.value,
													});
												}}
												className={classes.searchBar}
												style={{ border: "1px solid gray" }}
											/>
										</Mui.Grid>
									</Mui.Grid>
								</Mui.Hidden>
								{showSuccessBanner && (
									<Mui.Grid item xs={12}>
										<MuiAlert severity="success" onClose={() => this.setState({ showSuccessBanner: false })}>
											Team member added successfully!
										</MuiAlert>
									</Mui.Grid>
								)}
								<Mui.Grid item>
									<AgentTable agents={agents} maxForTeams={maxForTeams}/>
								</Mui.Grid>
							</Mui.Grid>
						)}
					</Mui.Grid>
				</FullPageCard>
			</DashboardLayout>
		);
	}

	public render() {
		return env.refreshTeam ? this.renderRefresh() : this.renderLegacy();
	}
}

export const SettingsAgentsPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
